/**
 * Reveals a buffered text response in batches of tokens to simulate a live
 * streaming experience in the UI. Useful when the backend returns the full
 * response in a single payload but we still want a token-by-token reveal.
 *
 * @param {string} fullText - The complete text to reveal progressively.
 * @param {Object} [options]
 * @param {number} [options.tokensPerTick=3] - How many whitespace-separated tokens to reveal per tick.
 * @param {number} [options.delayMs=25] - Delay between ticks in milliseconds.
 * @param {(currentText: string) => void} [options.onUpdate] - Called on each tick with the accumulated text.
 * @param {() => boolean} [options.isAborted] - Returns true to stop the typewriter early.
 * @param {{ current: any }} [options.intervalRef] - Ref to track the active interval so callers can clear it externally.
 * @returns {Promise<void>} Resolves when the full text has been revealed (or aborted).
 */
export const simulateTypewriterStream = (
  fullText,
  {
    tokensPerTick = 3,
    delayMs = 25,
    onUpdate,
    isAborted,
    intervalRef,
  } = {}
) => {
  return new Promise((resolve) => {
    if (intervalRef?.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!fullText) {
      resolve();
      return;
    }

    const tokens = fullText.split(/(\s+)/);
    let currentIndex = 0;
    let currentText = "";

    const id = setInterval(() => {
      if (typeof isAborted === "function" && isAborted()) {
        clearInterval(id);
        if (intervalRef) intervalRef.current = null;
        resolve();
        return;
      }

      if (currentIndex >= tokens.length) {
        clearInterval(id);
        if (intervalRef) intervalRef.current = null;
        if (typeof onUpdate === "function") onUpdate(fullText);
        resolve();
        return;
      }

      const batch = tokens.slice(currentIndex, currentIndex + tokensPerTick).join("");
      currentText += batch;
      currentIndex += tokensPerTick;

      if (typeof onUpdate === "function") onUpdate(currentText);
    }, delayMs);

    if (intervalRef) intervalRef.current = id;
  });
};
